import React, { PureComponent } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { store } from 'redux-rest-reducer';
import ReactTable from 'react-table';
import withFixedColumns from 'react-table-hoc-fixed-columns';

import 'react-table/react-table.css';
import 'react-table-hoc-fixed-columns/lib/styles.css';
import styled from 'styled-components';
import { Button, Row, Text } from '@cuemath/comps';
import { FULL_DATE } from 'constants/date';
import { format } from 'date-fns';
import COLORS from 'constants/colors';
import { openModal } from 'actions/modal';
import { patch as confirmPaymentStatus } from 'actions/student-payment-link';
import { invalidateItem as invalidateStudentAccountData } from 'actions/student-account';
import { STUDENT_PAYMENT_LINK_REFUND_URL, STUDENT_FEE_REFUND_URL } from 'constants/api';
import { PAYMENT_STATUS } from 'constants/form';
import PropTypes from 'prop-types';

const ReactTableFixedColumns = withFixedColumns(ReactTable);

const TableWrapper = styled.div`
  margin: 5px;
`;
const ButtonWrapper = styled(Button)`
  margin: 0 5px;
`;

const Head = styled(Row)`
  width: 100%;
  padding: 10px 0 10px 10px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const Tag = styled.div`
  background: ${COLORS.LINK};
  padding: 0px 5px;
  font-size: 10px;
  color: white;
  font-weight: 900;
  border-radius: 4px;
`;

const RowWrapper = styled(Row)`
  gap: 10px;
`;

const NA = 'NA';

class PaymentHistory extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      paymentDetails: null,
      paymentPatch: store.patch(),
    };
  }

  static defaultProps = {
    paymentHistory: [],
    studentId: '',
  };

  static propTypes = {
    onCreateNewPaymentLink: PropTypes.func.isRequired,
    onCreatePaymentLink: PropTypes.func.isRequired,
    onAddOfflinePayment: PropTypes.func.isRequired,
    paymentHistory: PropTypes.arrayOf(PropTypes.shape({})),
    studentId: PropTypes.string,
  };

  render() {
    const {
      paymentHistory = [],
      studentId = '',
      onCreateNewPaymentLink,
      onCreatePaymentLink,
      onAddOfflinePayment,
    } = this.props;

    const tsToDate = value => {
      return value ? format(value * 1000, FULL_DATE) : NA;
    };

    const columnsData = [
      {
        Header: 'Created On',
        accessor: 'created_on_ts',
        Cell: ({ value, row }) => {
          const { _original: original } = row || {};
          const { is_upgrade_payment: isUpgradePayment } = original || {};

          return isUpgradePayment ? (
            <RowWrapper>
              <Tag>Upgrade</Tag>
              <div>{tsToDate(value)}</div>
            </RowWrapper>
          ) : (
            tsToDate(value)
          );
        },
      },
      {
        Header: 'Payment ID',
        accessor: 'id',
        width: 300,
      },
      {
        Header: 'Paid on',
        accessor: 'paid_on',
        Cell: ({ value }) => tsToDate(value),
      },
      {
        Header: 'Status',
        accessor: 'state',
        width: 100,
      },
      {
        Header: 'Course',
        Cell: row => {
          let courseData = '';
          courseData = row.original.courses.map(elem => elem.name).join(',');
          return courseData ? courseData : '-';
        },
      },
      {
        Header: 'Amount',
        accessor: 'amount',
        width: 100,
      },
      {
        Header: 'Currency',
        accessor: 'currency',
        width: 100,
      },
      {
        Header: 'Tenure',
        accessor: 'duration',
        width: 100,
      },
      {
        Header: 'Number of classes',
        accessor: 'no_of_classes',
        Cell: ({ value, row }) => {
          const { _original: original } = row || {};
          const { no_of_classes: noOfClasses } = original || {};
          return <>{noOfClasses}</>;
        },
      },
      {
        Header: 'Classes extension category',
        accessor: 'classes_extension_category',
        Cell: ({ value, row }) => {
          const { _original: original } = row || {};
          const { classes_extension_category: classesExtensionCategory } = original || {};
          return (
            <RowWrapper>
              <Tag>{classesExtensionCategory}</Tag>
            </RowWrapper>
          );
        },
      },
      {
        Header: 'Product Type',
        accessor: 'product_type',
        width: 100,
      },
      {
        Header: 'Payment type',
        accessor: 'payment_type',
        width: 200,
      },
      {
        Header: 'Invoice ID',
        accessor: 'invoice_id',
        width: 300,
      },
      {
        Header: 'Credits Applied',
        accessor: 'credits_applied',
      },
      {
        Header: 'Credits Used',
        accessor: 'credits_used',
      },
      {
        Header: 'Cuemath Tax invoice',
        width: 180,
        Cell: row => {
          return row.original.cuemath_invoice_url ? (
            <a
              href={row.original.cuemath_invoice_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Invoice
            </a>
          ) : (
            <Text>Not available</Text>
          );
        },
      },
      {
        Header: 'Refunded',
        width: 100,
        Cell: row => {
          return row.original.is_refunded ? <Text> True </Text> : <Text> False </Text>;
        },
      },
      {
        Header: 'Refund Link',
        width: 150,
        Cell: row => {
          return row.original.state === PAYMENT_STATUS.CONFIRMED ? (
            <a
              href={
                (row.original.is_student_fee
                  ? STUDENT_FEE_REFUND_URL
                  : STUDENT_PAYMENT_LINK_REFUND_URL) + row.original.id
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              Create Refund
            </a>
          ) : (
            <Text> NA </Text>
          );
        },
      },
    ];

    const tableColumns = [
      {
        Header: ``,
        columns: columnsData,
      },
    ];

    return (
      <TableWrapper>
        <Head>
          <ButtonWrapper
            onClick={() => onAddOfflinePayment({ id: studentId })}
            label="Add Offline Payments"
          />
          <ButtonWrapper
            onClick={() => onCreatePaymentLink({ id: studentId })}
            label="Subscription Payment Link"
          />
          <ButtonWrapper
            onClick={() => onCreateNewPaymentLink({ id: studentId })}
            label="One Time Payment Link"
          />
        </Head>
        <ReactTableFixedColumns
          data={paymentHistory || []}
          columns={tableColumns}
          defaultPageSize={
            paymentHistory && paymentHistory.length !== 0 ? paymentHistory.length : 4
          }
          style={{ maxHeight: 300, textAlign: 'center' }}
          className="-striped -highlight"
        />
      </TableWrapper>
    );
  }
}
export default connect(null, dispatch => ({
  actions: bindActionCreators(
    {
      invalidateStudentAccountData,
      openModal,
      confirmPaymentStatus,
    },
    dispatch,
  ),
}))(PaymentHistory);
